import { Link } from 'react-router';
import { useCanOpen } from '@/hooks/useCanOpen';
import { formatDate } from '@/utils/dateUtils';
import { activityGapDurationLine } from '@/types/activityGap';
import { getActivityTypeLabels } from '@/types/activities';
import { DATE_PREFIX, FieldRow, clockTime, readString } from './payloadFields';

interface ActivityGapPayloadProps {
  payload: Record<string, unknown>;
  i18n: {
    _: (d: { id: string; message: string; values?: Record<string, unknown> }) => string;
  };
}

function readResolution(payload: Record<string, unknown>) {
  const raw = payload.resolution;
  if (typeof raw !== 'object' || raw === null) return undefined;
  const record = raw as Record<string, unknown>;
  return {
    activityType: readString(record, 'activity_type'),
    time: clockTime(readString(record, 'activity_time')),
    recordedBy: readString(record, 'recorded_by_name'),
  };
}

/**
 * Renders an `activity_gap` workflow item: a group with children present has no
 * entry for longer than the threshold. Leads with the group; no child is named.
 * Times are org-local RFC 3339 strings read as text.
 */
export function ActivityGapPayload({ payload, i18n }: ActivityGapPayloadProps) {
  const canOpen = useCanOpen();
  const groupName = readString(payload, 'group_name');
  const leadName = readString(payload, 'lead_name');
  const headName = readString(payload, 'head_name');
  const day = readString(payload, 'day');
  const validDay = day && DATE_PREFIX.test(day) ? day.slice(0, 10) : undefined;
  const presentChildren =
    typeof payload.present_children === 'number' ? payload.present_children : undefined;
  const lastTime = clockTime(readString(payload, 'last_activity_time'));
  const lastType = readString(payload, 'last_activity_type');
  const dueAt = clockTime(readString(payload, 'due_at'));
  const handedAt = clockTime(readString(payload, 'handed_to_admins_at'));
  const endedAt = clockTime(readString(payload, 'ended_at'));
  const resolution = readResolution(payload);
  const durationLine = activityGapDurationLine(i18n, payload);
  const typeLabels = getActivityTypeLabels(i18n);

  const withDate = (time: string) =>
    validDay
      ? i18n._({
          id: 'workflow.payload.activityGap.dateTime',
          message: '{date}, {time}',
          values: { date: formatDate(validDay), time },
        })
      : time;

  const lastEntryText = (() => {
    if (!lastTime) {
      return i18n._({ id: 'workflow.payload.activityGap.noEntryToday', message: 'None today' });
    }
    const typeLabel = lastType ? typeLabels[lastType] : undefined;
    return typeLabel
      ? i18n._({
          id: 'workflow.payload.activityGap.lastEntryValue',
          message: '{type} at {time}',
          values: { type: typeLabel, time: lastTime },
        })
      : withDate(lastTime);
  })();

  const resolutionLine = (() => {
    if (!resolution?.time) return undefined;
    const values = {
      type: (resolution.activityType && (typeLabels[resolution.activityType] ?? resolution.activityType)) || '',
      name: resolution.recordedBy,
      time: resolution.time,
    };
    return resolution.recordedBy
      ? i18n._({
          id: 'workflow.payload.activityGap.resolvedBy',
          message: 'Done: entry ({type}) by {name} at {time}',
          values,
        })
      : i18n._({
          id: 'workflow.payload.activityGap.resolved',
          message: 'Done: entry ({type}) at {time}',
          values,
        });
  })();

  const entriesLink =
    validDay && canOpen('/activities')
      ? `/activities?date_from=${validDay}&date_to=${validDay}`
      : undefined;

  return (
    <div className="space-y-4">
      <FieldRow label={i18n._({ id: 'workflow.payload.activityGap.group', message: 'Group' })}>
        <p className="text-sm font-medium" data-testid="activity-gap-group">
          {groupName ?? '—'}
        </p>
        {durationLine && (
          <p className="text-sm" data-testid="activity-gap-duration">
            {durationLine}
          </p>
        )}
        {entriesLink && (
          <Link to={entriesLink} className="text-sm text-primary underline underline-offset-4">
            {i18n._({
              id: 'workflow.payload.activityGap.openActivities',
              message: 'Open the entries for this day',
            })}
          </Link>
        )}
      </FieldRow>

      {presentChildren !== undefined && (
        <FieldRow
          label={i18n._({ id: 'workflow.payload.activityGap.presentChildren', message: 'Children present' })}
        >
          <p className="text-sm" data-testid="activity-gap-present">
            {presentChildren}
          </p>
        </FieldRow>
      )}

      <FieldRow
        label={i18n._({ id: 'workflow.payload.activityGap.lastEntry', message: 'Last entry' })}
      >
        <p className="text-sm" data-testid="activity-gap-last-entry">
          {lastEntryText}
        </p>
      </FieldRow>

      {dueAt && (
        <FieldRow label={i18n._({ id: 'workflow.payload.activityGap.dueAt', message: 'Due' })}>
          <p className="text-sm">{withDate(dueAt)}</p>
        </FieldRow>
      )}

      <FieldRow
        label={i18n._({ id: 'workflow.payload.activityGap.groupLead', message: 'Group lead' })}
      >
        <p className="text-sm">
          {leadName ??
            i18n._({
              id: 'workflow.payload.activityGap.noGroupLead',
              message: 'No group lead set',
            })}
        </p>
      </FieldRow>

      <FieldRow
        label={i18n._({ id: 'workflow.payload.activityGap.head', message: 'Daycare management' })}
      >
        <p className="text-sm">
          {headName ??
            i18n._({
              id: 'workflow.payload.activityGap.directToAdmin',
              message: 'Straight to the administration',
            })}
        </p>
      </FieldRow>

      {handedAt && (
        <FieldRow
          label={i18n._({
            id: 'workflow.payload.activityGap.handedToAdmins',
            message: 'Handed to the administration',
          })}
        >
          <p className="text-sm">{withDate(handedAt)}</p>
        </FieldRow>
      )}

      {endedAt && (
        <FieldRow label={i18n._({ id: 'workflow.payload.activityGap.endedAt', message: 'Ended' })}>
          <p className="text-sm">{withDate(endedAt)}</p>
        </FieldRow>
      )}

      {resolutionLine && (
        <p className="text-sm font-medium" data-testid="activity-gap-resolution">
          {resolutionLine}
        </p>
      )}
    </div>
  );
}
